import React, { useState, useEffect } from 'react';
import TimeseriesChart from './TimeseriesChart';
import { STACItem } from '../types/stac';
import { RWANDA_CONFIG, getAnalysisPreset } from '../config/rwanda';

interface TimeseriesAnalysisProps {
  selectedItems: STACItem[];
  analysisType: string;
  timeRange: { start: Date; end: Date };
  province: string | null;
}

const TimeseriesAnalysis: React.FC<TimeseriesAnalysisProps> = ({
  selectedItems,
  analysisType,
  timeRange,
  province
}) => {
  const [analysisData, setAnalysisData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (selectedItems.length > 0) {
      performAnalysis();
    }
  }, [selectedItems, analysisType, timeRange]);

  const performAnalysis = async () => {
    setLoading(true);
    try {
      // Get the current province or use default bbox
      let bbox: [number, number, number, number] | undefined;
      
      if (province) {
        const provinceConfig = RWANDA_CONFIG.PROVINCES.find(p => p.name === province);
        bbox = provinceConfig?.bbox;
      }

      console.log('Performing analysis:', { 
        analysisType, 
        itemCount: selectedItems.length,
        bbox 
      });
      
      // Simulate analysis data - removed unused index parameter
      const mockData = selectedItems.map((item) => ({
        date: item.properties.datetime,
        ndvi: 0.3 + Math.random() * 0.4,
        evi: 0.2 + Math.random() * 0.3,
        item: item.id
      }));
      
      setAnalysisData(mockData);
    } catch (error) {
      console.error('Analysis failed:', error);
    } finally {
      setLoading(false);
    }
  };

  const preset = getAnalysisPreset(analysisType);

  return (
    <div className="h-full flex flex-col bg-white">
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold">Time Series Analysis</h3>
          <div className="text-sm text-gray-500">
            {preset?.name || analysisType} • {selectedItems.length} items
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-2 text-gray-600">Analyzing data...</span>
          </div>
        ) : analysisData.length > 0 ? (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="text-sm text-gray-600 mb-2">Analysis Results</div>
              <div className="grid grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="font-medium">Mean NDVI</div>
                  <div className="text-green-600">
                    {(analysisData.reduce((acc, d) => acc + d.ndvi, 0) / analysisData.length).toFixed(3)}
                  </div>
                </div>
                <div>
                  <div className="font-medium">Trend</div>
                  <div className="text-blue-600">↗ Increasing</div>
                </div>
                <div>
                  <div className="font-medium">Data Points</div>
                  <div className="text-gray-600">{analysisData.length}</div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">Seasonal Context</h4>
              <div className="space-y-2 text-sm">
                {Object.entries(RWANDA_CONFIG.SEASONS).map(([season, config]) => {
                  const currentMonth = new Date().getMonth() + 1;
                  const isCurrentSeason = config.months.includes(currentMonth);
                  
                  return (
                    <div 
                      key={season}
                      className={`flex justify-between ${isCurrentSeason ? 'font-medium text-blue-900' : 'text-blue-700'}`}
                    >
                      <span>{config.name}</span>
                      {isCurrentSeason && <span className="text-green-600">● Current</span>}
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-medium text-gray-900 mb-2">Data Sources</h4>
              <div className="space-y-1 text-sm">
                {Object.entries(
                  selectedItems.reduce((acc, item) => {
                    acc[item.collection] = (acc[item.collection] || 0) + 1;
                    return acc;
                  }, {} as Record<string, number>)
                ).map(([collection, count]) => (
                  <div key={collection} className="flex justify-between">
                    <span className="capitalize">{collection.replace(/-/g, ' ')}</span>
                    <span className="text-gray-600">{count} items</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            <div className="text-4xl mb-2">📊</div>
            <div>Select satellite data to begin analysis</div>
            <div className="text-sm mt-1">
              Choose an area and time range in the search panel
            </div>
          </div>
        )}
      </div>

      {analysisData.length > 0 && (
        <div className="p-4">
          <TimeseriesChart 
            data={analysisData.map(d => ({
              date: d.date,
              values: { ndvi: d.ndvi, evi: d.evi }
            }))}
            analysisType={analysisType}
          />
        </div>
      )}
    </div>
  );
};

export default TimeseriesAnalysis;